import {
  IBoltTaskTemplate,
  TaskExecutionTypeFieldSchema,
  useBoltApiClient,
} from "@bolt/core";
import {
  AppToasterV2,
  FieldFormV2,
  FieldFormV2Module,
  Footer,
  FieldV2,
  SwitchFieldProps,
  ActionButtons,
  fieldTypes,
} from "@ucl/library";
import { useEffect, useRef, useState, useMemo } from "react";
import { BoltTaskTemplates } from "./CreateTaskTemplateForm";
import { RichTextEditorsOutputTextTypes } from "../../types/globalTypes";
import {
  useTaskTemplateSubmit,
  TaskTemplateSubmitMode,
} from "./useTaskTemplateSubmit";
import { createBoltTaskApiClient } from "../../lib/apiClients/boltTaskApiClient";
import { EditBoltTaskTemplateFormContainer } from "./builders/EditBoltTaskTemplateFormContainer";

export interface EditBoltTaskTemplateFormProps {
  // extends BoltInteraction<
  //   {
  //     onSubmitSuccessfully: (
  //       taskTemplate: IBoltTaskTemplate | IBoltTaskTemplate[]
  //     ) => Promise<void>;
  //   },
  //   {
  //     hasEditPermission: boolean;
  //   }
  // > {
  // This is the id to retrieve the task batch
  taskTemplateBatchId: string;
  apiBaseUrl: string;
  assigneeOdataUrl: string;
  getAuthRequestHeader: () => Promise<Record<string, string> | undefined>;
  // currentUser: { id: string };
  richTextEditorsOutputTextType?: RichTextEditorsOutputTextTypes;
  onSave?: () => void;
  onCancel?: () => void;
  entityTypeOptions?: fieldTypes.Option<string>[] | [];
}

export const EditBoltTaskTemplateForm: React.FC<
  EditBoltTaskTemplateFormProps
> = ({
  apiBaseUrl,
  assigneeOdataUrl,
  getAuthRequestHeader,
  taskTemplateBatchId,
  richTextEditorsOutputTextType,
  onSave,
  onCancel,
  entityTypeOptions,
}) => {
  const taskTemplateFormRef =
    useRef<FieldFormV2Module<BoltTaskTemplates>>(null);
  const boltApiClient = useBoltApiClient();
  const taskApiClient = createBoltTaskApiClient(boltApiClient);
  const { submitTaskTemplates } = useTaskTemplateSubmit(
    TaskTemplateSubmitMode.EDIT
  );
  const [taskExecutionTypeData, setTaskExecutionTypeData] =
    useState<TaskExecutionTypeFieldSchema>({});
  const [localTaskTemplates, setLocalTaskTemplates] =
    useState<IBoltTaskTemplate[]>();
  const [errors, _setErrors] = useState<{ [key: string]: string[] }>();

  useEffect(() => {
    let isCancelled = false;

    const loadTaskTemplates = async () => {
      try {
        const taskTemplates =
          await taskApiClient.getTaskTypesByBatchId(taskTemplateBatchId);
        if (!isCancelled) {
          setLocalTaskTemplates(taskTemplates);
        }
      } catch (error) {
        if (
          !isCancelled &&
          localTaskTemplates &&
          localTaskTemplates.length > 0
        ) {
          console.error("Failed to load task templates:", error);
          AppToasterV2.showFailedActionToaster(
            "Failed to load task template data."
          );
        }
      }
    };

    loadTaskTemplates();

    return () => {
      isCancelled = true;
    };
  }, [taskTemplateBatchId]);

  const derivedActionTypes = useMemo(() => {
    if (!localTaskTemplates?.[0]?.taskExecutionTypeFields) return [];
    return Object.keys(localTaskTemplates[0].taskExecutionTypeFields);
  }, [localTaskTemplates]);

  useEffect(() => {
    if (localTaskTemplates?.[0]?.taskExecutionTypeFields) {
      setTaskExecutionTypeData(localTaskTemplates[0].taskExecutionTypeFields);
    }
  }, [localTaskTemplates]);

  useEffect(() => {
    if (localTaskTemplates && derivedActionTypes.length > 0) {
      const parentTask = localTaskTemplates[0];
      const hasActionTypes = parentTask.taskExecutionType;

      if (!hasActionTypes) {
        setLocalTaskTemplates((prev) => {
          if (!prev) return prev;
          const updated = [...prev];
          updated[0] = {
            ...updated[0],
            taskExecutionType: parentTask.taskExecutionType,
            // taskExecutionTypes: derivedActionTypes,
          };
          return updated;
        });
      }
    }
  }, [derivedActionTypes]); // Only depend on derivedActionTypes, not localTaskTypes

  return (
    <div className="bolt-task-type-form-wrapper">
      {localTaskTemplates && (
        <FieldFormV2<BoltTaskTemplates>
          ref={taskTemplateFormRef}
          onFormSubmit={() =>
            submitTaskTemplates(
              localTaskTemplates,
              taskExecutionTypeData,
              onSave ? async () => await onSave() : async () => {}
            )
          }
        >
          <div className="bolt-task-type-form-body">
            <div className="bolt-task-type-form-body-content">
              <EditBoltTaskTemplateFormContainer
                taskTemplates={localTaskTemplates}
                updateTaskTemplates={(type) => setLocalTaskTemplates(type)}
                apiBaseUrl={apiBaseUrl}
                assigneeOdataUrl={assigneeOdataUrl}
                getAuthRequestHeader={getAuthRequestHeader}
                richTextEditorsOutputTextType={richTextEditorsOutputTextType}
                errors={errors}
                taskExecutionTypeData={taskExecutionTypeData}
                updateTaskExecutionTypeData={(updatedModel) =>
                  setTaskExecutionTypeData(updatedModel)
                }
                entityTypeOptions={entityTypeOptions}
                showWorkflowTypeSelector={false}
              />
            </div>
            <FieldV2
              fieldProps={
                {
                  type: "Switch",
                  className: "is-active-switch",
                  fieldKey: "BoltTaskType__isActive",
                  label: "Is Active",
                  value: localTaskTemplates.at(0)?.isActive ?? true,
                  large: true,
                  onSubmit: (value) => {
                    if (typeof value === "boolean" && localTaskTemplates) {
                      setLocalTaskTemplates((prev) => {
                        if (!prev) return prev;
                        const updated = [...prev];
                        updated[0] = { ...updated[0], isActive: value };
                        return updated;
                      });
                    }
                  },
                } as SwitchFieldProps
              }
            />
          </div>
        </FieldFormV2>
      )}
      <Footer>
        <div className="ucl-footer-children">
          <ActionButtons
            actions={[
              {
                text: "Cancel",
                onActionClick: onCancel,
              },
              {
                text: "Save",
                isPrimary: true,
                onActionClick: () => {
                  if (taskTemplateFormRef.current) {
                    taskTemplateFormRef.current.submit();
                  }
                },
              },
            ]}
          />
        </div>
      </Footer>
    </div>
  );
};
